import React, { memo } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

// Icon configuration (complete SVG JSX)
const ICONS = {
    sync: (
        <svg className="fill-current shrink-0" viewBox="0 0 16 16">
            <path d="M4.3 4.5c1.9-1.9 5.1-1.9 7 0 .7.7 1.2 1.7 1.4 2.7l2-.3c-.2-1.5-.9-2.8-1.9-3.8C10.1.4 5.7.4 2.9 3.1L.7.9 0 7.3l6.4-.7-2.1-2.1zM15.6 8.7l-6.4.7 2.1 2.1c-1.9 1.9-5.1 1.9-7 0-.7-.7-1.2-1.7-1.4-2.7l-2 .3c.2 1.5.9 2.8 1.9 3.8 1.4 1.4 3.1 2 4.9 2 1.8 0 3.6-.7 4.9-2l2.2 2.2.8-6.4z" />
        </svg>
    ),
    plus: (
        <svg className="fill-current shrink-0" viewBox="0 0 16 16">
            <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
        </svg>
    ),
    check: (
        <svg className="fill-current shrink-0" viewBox="0 0 16 16">
            <path d="M14.3 2.3L5 11.6 1.7 8.3c-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4l4 4c.2.2.4.3.7.3.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4-.4-.4-1-.4-1.4 0z" />
        </svg>
    ),
    pencil: (
        <svg className="fill-current shrink-0" viewBox="0 0 16 16">
            <path d="M11.7.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM4.6 14H2v-2.6l6-6L10.6 8l-6 6zM12 6.6L9.4 4 11 2.4 13.6 5 12 6.6z" />
        </svg>
    ),
    trash: (
        <svg className="fill-current shrink-0" viewBox="0 0 16 16">
            <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
        </svg>
    ),
};

// Style configuration
const STYLES = {
    base: [
        "rounded-lg",
        "border",
        "flex",
        "items-center",
        "justify-center",
        "shrink-0",
        "transition-colors",
    ],
    variants: {
        primary: [
            "text-gray-500 dark:text-gray-300",
            "hover:text-black dark:hover:text-white",
            "bg-white",
            "dark:bg-gray-800",
            "border-gray-300 dark:border-gray-500",
            "hover:border-gray-400 dark:hover:border-gray-300/85",
            "dark:border-gray-700/60",
            "dark:hover:border-gray-600",
        ],
        "primary-red": [
            "text-red-500",
            "hover:text-red-500",
            "bg-white",
            "dark:bg-gray-800",
            "border-gray-300 dark:border-gray-500",
            "hover:border-red-400 dark:hover:border-red-400",
            "dark:border-gray-700/60",
            "dark:hover:border-gray-600",
        ],
    },
    sizes: {
        28: ["w-[28px]", "h-[28px]"],
        36: ["w-9", "h-9"],
    },
    disabled: ["opacity-50", "cursor-not-allowed"],
};

// Default prop values
const DEFAULTS = {
    variant: "primary",
    icon: "pencil",
    size: 28,
    className: "",
    disabled: false,
    type: "button",
};

const ActionButton = React.forwardRef(function ActionButton(
    {
        children,
        variant = DEFAULTS.variant,
        icon = DEFAULTS.icon,
        size = DEFAULTS.size,
        className = DEFAULTS.className,
        onClick,
        title,
        disabled = DEFAULTS.disabled,
        type = DEFAULTS.type,
        ...rest
    },
    ref
) {
    // Configure icon
    const iconConfig = ICONS[icon] || null;
    const iconSize = size / 2; // Icon size relative to button

    // Dynamic classes
    const classes = clsx(
        STYLES.base,
        STYLES.variants[variant] || STYLES.variants.primary,
        STYLES.sizes[size] || STYLES.sizes[28],
        disabled && STYLES.disabled,
        className
    );

    return (
        <button
            ref={ref}
            type={type}
            className={classes}
            aria-disabled={disabled}
            disabled={disabled}
            title={title}
            onClick={onClick}
            {...rest}
        >
            {iconConfig &&
                React.cloneElement(iconConfig, {
                    width: iconSize,
                    height: iconSize,
                })}
            {children}
        </button>
    );
});

ActionButton.propTypes = {
    children: PropTypes.node,
    variant: PropTypes.oneOf(Object.keys(STYLES.variants)),
    icon: PropTypes.oneOf(Object.keys(ICONS)),
    size: PropTypes.oneOf(Object.keys(STYLES.sizes).map(Number)),
    className: PropTypes.string,
    onClick: PropTypes.func,
    title: PropTypes.string,
    disabled: PropTypes.bool,
    type: PropTypes.oneOf(["button", "submit", "reset"]),
};

ActionButton.displayName = "ActionButton";

export default memo(ActionButton);
